import { FileDown, Award, Trophy, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

interface HomePageProps {
  navigateToQuotes: () => void;
  navigateToMedalQuotes: () => void;
  navigateToTrophyQuotes?: () => void;
  navigateToTombstoneQuotes?: () => void;
  navigateToPlacaQuotes?: () => void;
  canManageQuotes: boolean;
  canManageMedals: boolean;
  canManageTrophies?: boolean;
  canManageTombstones?: boolean;
  canManagePlacas?: boolean;
  chapasUnreadCount: number;
  medalUnreadCount: number;
  trophyUnreadCount?: number;
  tombstoneUnreadCount?: number;
  placaUnreadCount?: number;
}

export function HomePage({ navigateToQuotes, navigateToMedalQuotes, navigateToTrophyQuotes, navigateToTombstoneQuotes, navigateToPlacaQuotes, canManageQuotes, canManageMedals, canManageTrophies, canManageTombstones, canManagePlacas, chapasUnreadCount, medalUnreadCount, trophyUnreadCount = 0, tombstoneUnreadCount = 0, placaUnreadCount = 0 }: HomePageProps) {
  const visibleCards = [canManageQuotes, canManageMedals, canManageTrophies, canManageTombstones, canManagePlacas].filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Bem-vindo ao BackOffice</h2>
        <p className="text-sm text-muted-foreground">
          Acesse os orçamentos e módulos administrativos.
        </p>
      </div>

      <div className={`grid gap-4 ${visibleCards >= 5 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5' : visibleCards === 4 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : visibleCards === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : visibleCards === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 max-w-md'}`}>
        {canManageQuotes && (
          <Card className="shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={navigateToQuotes}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <FileDown className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-base">Orçamentos de Chapas</CardTitle>
                </div>
                {chapasUnreadCount > 0 && (
                  <Badge className="text-xs px-2.5 py-1 whitespace-nowrap bg-primary/15 text-primary border-primary/20 hover:bg-primary/20">
                    {chapasUnreadCount} {chapasUnreadCount === 1 ? 'novo' : 'novos'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground mb-4">
                Gerencie orçamentos de chapas acrílicas recebidos pelos clientes.
              </p>
              <Button className="gap-2 w-full">
                Acessar
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {canManageMedals && (
          <Card className="shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={navigateToMedalQuotes}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <Award className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-base">Orçamentos de Medalhas</CardTitle>
                </div>
                {medalUnreadCount > 0 && (
                  <Badge className="text-xs px-2.5 py-1 whitespace-nowrap bg-primary/15 text-primary border-primary/20 hover:bg-primary/20">
                    {medalUnreadCount} {medalUnreadCount === 1 ? 'novo' : 'novos'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground mb-4">
                Gerencie orçamentos de medalhas personalizadas recebidos pelos clientes.
              </p>
              <Button className="gap-2 w-full">
                Acessar
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {canManageTrophies && (
          <Card className="shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={navigateToTrophyQuotes}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <Trophy className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-base">Orçamentos de Troféus</CardTitle>
                </div>
                {trophyUnreadCount > 0 && (
                  <Badge className="text-xs px-2.5 py-1 whitespace-nowrap bg-primary/15 text-primary border-primary/20 hover:bg-primary/20">
                    {trophyUnreadCount} {trophyUnreadCount === 1 ? 'novo' : 'novos'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground mb-4">
                Gerencie orçamentos de troféus personalizados recebidos pelos clientes.
              </p>
              <Button className="gap-2 w-full">
                Acessar
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {canManageTombstones && (
          <Card className="shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={navigateToTombstoneQuotes}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-primary">
                      <rect x="5" y="2" width="14" height="17" rx="2.5"/>
                      <path d="M8 22h8"/>
                      <path d="M10 19v3"/>
                      <path d="M14 19v3"/>
                      <path d="M12 7.5l1.1 2.2 2.4.35-1.75 1.7.4 2.4L12 13l-2.15 1.15.4-2.4-1.75-1.7 2.4-.35Z" opacity="0.4"/>
                    </svg>
                  </div>
                  <CardTitle className="text-base">Orçamentos de Tombstones</CardTitle>
                </div>
                {tombstoneUnreadCount > 0 && (
                  <Badge className="text-xs px-2.5 py-1 whitespace-nowrap bg-primary/15 text-primary border-primary/20 hover:bg-primary/20">
                    {tombstoneUnreadCount} {tombstoneUnreadCount === 1 ? 'novo' : 'novos'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground mb-4">
                Gerencie orçamentos de tombstones personalizados recebidos pelos clientes.
              </p>
              <Button className="gap-2 w-full">
                Acessar
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {canManagePlacas && (
          <Card className="shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={navigateToPlacaQuotes}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-primary">
                      <rect x="4" y="3" width="16" height="18" rx="2"/>
                      <path d="M9 8h6" opacity="0.4"/>
                      <path d="M9 11h6" opacity="0.4"/>
                      <circle cx="12" cy="16" r="2"/>
                    </svg>
                  </div>
                  <CardTitle className="text-base">Orçamentos de Placas</CardTitle>
                </div>
                {placaUnreadCount > 0 && (
                  <Badge className="text-xs px-2.5 py-1 whitespace-nowrap bg-primary/15 text-primary border-primary/20 hover:bg-primary/20">
                    {placaUnreadCount} {placaUnreadCount === 1 ? 'novo' : 'novos'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground mb-4">
                Gerencie orçamentos de placas de premiação recebidos pelos clientes.
              </p>
              <Button className="gap-2 w-full">
                Acessar
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
